import { GridApi } from 'ag-grid-community'
import { Form, Input } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCrossroadsEntityGrid } from '../../../api/hooks/useCrossroadsEntityGrid'
import { ActionButtons, CreateDrawer, GridContainer } from './components'
import { useMasterDataCRUD, useMasterDataGrid } from './hooks'
import { MasterDataGridProps } from './types'

/**
 * Main MasterDataGrid coordinator component
 * Orchestrates all the extracted components and hooks for master data management
 */
const PAGE_SIZE_STORAGE_KEY = 'master-data-grid-page-size'
const DEFAULT_PAGE_SIZE = 500
const PAGE_SIZE_OPTIONS = [100, 250, 500, 1000, 2500, 5000]

export const MasterDataGrid: React.FC<MasterDataGridProps> = ({
  entityType,
  filterInfo,
}) => {
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [createForm] = Form.useForm()
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY)
    const parsed = stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE
    return Number.isNaN(parsed) ? DEFAULT_PAGE_SIZE : parsed
  })

  // Fetch grid data to get column definitions from the schema
  const {
    data: gridData,
    isLoading,
    error,
    isError,
  } = useCrossroadsEntityGrid(entityType, true, pageSize)

  // Initialize grid configuration
  const gridProps = useMasterDataGrid({
    entityType,
    gridData,
    filterInfo,
    pageSize,
  })

  // Ref to access gridApi - populated by GraviGrid's externalRef
  const gridApiRef = useRef<GridApi | null>(null)

  // Initialize CRUD operations
  const { createMutation, handleUpdate, handleCreateSubmit } =
    useMasterDataCRUD({
      entityType,
      gridApi: gridProps.gridApi,
    })

  // Handle create drawer
  const handleCreateClick = () => {
    setCreateDrawerOpen(true)
  }

  const handleCreateDrawerClose = () => {
    setCreateDrawerOpen(false)
  }

  const handleCreateFormSubmit = async () => {
    const ok = await handleCreateSubmit(createForm)
    if (ok) {
      setCreateDrawerOpen(false)
    }
  }

  // Handle refresh - use ref populated by GraviGrid's externalRef
  const handleRefresh = useCallback(() => {
    if (gridApiRef.current) {
      gridApiRef.current.refreshServerSide({ purge: true })
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize))

    if (gridProps.gridApi) {
      gridProps.gridApi.paginationSetPageSize(pageSize)
      gridProps.gridApi.paginationGoToFirstPage()
      gridProps.gridApi.setServerSideDatasource(gridProps.serverSideDatasource)
      gridProps.gridApi.refreshServerSide({ purge: true })
    }
  }, [pageSize, gridProps.gridApi, gridProps.serverSideDatasource])

  // Generate action buttons
  const actionButtons = ActionButtons({
    entityType,
    onCreateClick: handleCreateClick,
    onRefresh: handleRefresh,
  })

  // Custom search bar overrides the default search handling to drive server-side search
  const customSearchBar = useMemo(
    () => (
      <Input
        placeholder={`Search ${entityType}...`}
        allowClear
        size='middle'
        value={gridProps.serverSearch}
        onChange={(e) => {
          const term = e.target.value
          gridProps.setServerSearch(term)
        }}
        onPressEnter={(e) => {
          const target = e.target as HTMLInputElement
          gridProps.setServerSearch(target.value || '')
        }}
        style={{ minWidth: 260 }}
      />
    ),
    [entityType, gridProps.serverSearch, gridProps.setServerSearch],
  )

  return (
    <>
      <GridContainer
        loading={isLoading}
        isError={isError}
        error={error}
        entityType={entityType}
        totalRows={gridProps.totalRows}
        gridOptions={gridProps.gridOptions}
        onUpdate={handleUpdate}
        actionButtons={actionButtons}
        customSearchBar={customSearchBar}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        gridApiRef={gridApiRef}
      />

      <CreateDrawer
        open={createDrawerOpen}
        onClose={handleCreateDrawerClose}
        onSubmit={handleCreateFormSubmit}
        form={createForm}
        entityType={entityType}
        loading={createMutation.isPending}
      />
    </>
  )
}
